import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { TRY, NUM, fmtDate } from "@/lib/format";
import { PageHeader, StatCard, Empty, Cell, Segmented } from "@/components/Shared";
import { DEFAULT_TERMS, validityState } from "@/components/QuoteTemplate";
import QuoteEditorDialog, { blankQuote } from "@/components/QuoteEditorDialog";
import StockSourceDialog from "@/components/StockSourceDialog";
import QuotePreviewDialog from "@/components/QuotePreviewDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, FileText, Eye, Pencil, UserPlus, FileUp, FileDown, Truck, UserRound, RotateCcw, X, Search, History, ReceiptText, Settings, Warehouse, Clock, CheckCircle2, XCircle, TrendingUp, Phone, CalendarDays, LayoutList, Table2, AlertTriangle, Hourglass } from "lucide-react";
import { cn } from "@/lib/utils";
import { RecordHistory } from "@/components/RecordHistory";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { isGenericContact } from "@/lib/contacts";
import InvoiceRecipientPicker, { blankRecipient, recipientError, recipientPayload } from "@/components/InvoiceRecipientPicker";

const STATUS = {
  taslak: { label: "Taslak", stripe: "bg-slate-400", cls: "border-slate-300 bg-slate-50 text-slate-600", dot: "bg-slate-400" },
  gonderildi: { label: "Gönderildi", stripe: "bg-sky-500", cls: "border-sky-300 bg-sky-50 text-sky-700", dot: "bg-sky-500" },
  degerlendiriliyor: { label: "Değerlendiriliyor", stripe: "bg-amber-500", cls: "border-amber-300 bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  kabul: { label: "Kabul Edildi", stripe: "bg-emerald-500", cls: "border-emerald-300 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
  red: { label: "Reddedildi", stripe: "bg-red-500", cls: "border-red-300 bg-red-50 text-red-600", dot: "bg-red-500" },
};
const statusOf = (s) => STATUS[s] || STATUS.taslak;
const PERIODS = [["all", "Tüm zamanlar"], ["month", "Bu ay"], ["3m", "Son 3 ay"], ["year", "Bu yıl"]];
const periodStart = (p) => {
  const d = new Date(); const y = d.getFullYear(); const m = d.getMonth();
  const iso = (x) => x.toISOString().slice(0, 10);
  if (p === "month") return iso(new Date(y, m, 1));
  if (p === "3m") return iso(new Date(y, m - 2, 1));
  if (p === "year") return `${y}-01-01`;
  return null;
};

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export default function Quotes() {
  const [list, setList] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [editor, setEditor] = useState({ open: false, initial: null });
  const [preview, setPreview] = useState(null);
  const [sendDlg, setSendDlg] = useState(null);
  const [sendWorker, setSendWorker] = useState("all");
  const [search, setSearch] = useState("");
  const [stFilter, setStFilter] = useState("all");
  const [period, setPeriod] = useState("all");
  const [sort, setSort] = useState("updated");
  const [view, setView] = useState(() => localStorage.getItem("ge_quote_view") || "card");
  useEffect(() => { localStorage.setItem("ge_quote_view", view); }, [view]);
  const [histQuote, setHistQuote] = useState(null);
  const [invDlg, setInvDlg] = useState(null);
  const [invMode, setInvMode] = useState("net");
  const [invItems, setInvItems] = useState([]);
  const [invRecipient, setInvRecipient] = useState({ title: "", vkn: "" });
  const [invRp, setInvRp] = useState(blankRecipient());
  const [invShowBrand, setInvShowBrand] = useState(false);
  const [quoteTerms, setQuoteTerms] = useState(DEFAULT_TERMS);
  const [termsDlg, setTermsDlg] = useState(false);
  const [termsDraft, setTermsDraft] = useState("");
  const [statusBusy, setStatusBusy] = useState(null);
  const [srcDlg, setSrcDlg] = useState(null);   // { q, resolve } — kabulde stok kaynağı seçimi
  const askStockSource = (q) => new Promise((resolve) => setSrcDlg({ q, resolve }));
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const load = () => api.get("/quotes").then((r) => setList((p) => (JSON.stringify(p) === JSON.stringify(r.data) ? p : r.data))).catch(() => {});
  useAutoRefresh(load);
  const loadContacts = () => api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
  useEffect(() => {
    load(); loadContacts();
    api.get("/users").then((r) => setWorkers(r.data.filter((u) => u.role === "worker"))).catch(() => {});
    api.get("/settings/quote-terms").then((r) => setQuoteTerms(r.data.terms || DEFAULT_TERMS)).catch(() => {});
  }, []);

  const openNew = (initial) => setEditor({ open: true, initial: initial || blankQuote() });
  const openEdit = (q) => setEditor({ open: true, initial: q });
  useEffect(() => {
    if (params.get("new") === "1") { openNew(); setParams({}, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  // Hızlı satış fişinden gelen taslak → editör dolu açılır (kaydedilmemiş)
  const location = useLocation();
  const draftKey = useRef(null);
  useEffect(() => {
    const d = location.state?.quoteDraft;
    if (!d || draftKey.current === location.key) return;
    draftKey.current = location.key;
    navigate(location.pathname, { replace: true, state: null });
    const { summary = {}, source, ...q } = d;
    openNew({ ...blankQuote(), ...q, _unsaved: true });
    const extra = [summary.skipped_returned ? `${summary.skipped_returned} iade kalemi aktarılmadı` : "", summary.missing_products ? `${summary.missing_products} ürün stokta bulunamadı (fiş fiyatı kullanıldı)` : "", summary.global_discount ? "fişteki ek indirim genel iskonto olarak eklendi" : ""].filter(Boolean).join(" · ");
    toast.success(`${source || "Fiş"} → teklif: ${summary.lines || q.items?.length || 0} kalem aktarıldı`, { description: `Kontrol edip Kaydet'e basın${extra ? ` · ${extra}` : ""}`, duration: 8000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const onSaved = (q) => { load(); loadContacts(); setPreview(q); };
  const del = async (id) => { if (await confirm("Teklifi sil?")) { await api.delete(`/quotes/${id}`); load(); } };
  const clearRestored = async (id) => { try { await api.post(`/quotes/${id}/clear-restored`); load(); } catch (e) { toast.error(apiError(e)); } };

  // Listeden hızlı durum değişimi (kabul → stok düşer + cariye işlenir; geri alınınca iade edilir)
  const changeStatus = async (q, status) => {
    if (status === q.status) return;
    let src = {};
    if (status === "kabul") { src = await askStockSource(q); if (!src) return; }
    if (q.status === "kabul" && !await confirm(`${q.quote_no} kabulden geri alınacak: düşülen stok iade edilir ve cari kaydı silinir. Onaylıyor musunuz?`)) return;
    setStatusBusy(q.id);
    try {
      const body = { ...q, ...src, status };
      ["id", "quote_no", "created_at", "updated_at"].forEach((k) => delete body[k]);
      await api.put(`/quotes/${q.id}`, body);
      toast.success(`${q.quote_no} → ${statusOf(status).label}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setStatusBusy(null); }
  };

  const invGeneric = !!invDlg && !invDlg.vat_exempt && isGenericContact(invDlg.customer_title);
  const openInvDlg = (q) => { setInvRp(blankRecipient()); setInvDlg(q); setInvMode("net"); setInvShowBrand(false); setInvItems((q.items || []).map((_, i) => i)); setInvRecipient({ title: q.invoice_recipient_title || "", vkn: q.invoice_recipient_vkn || "" }); };
  const toggleInvItem = (i) => setInvItems((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));
  const generateInvoice = async () => {
    if (!invDlg) return;
    if (!invItems.length) return toast.error("Faturaya en az bir kalem seçin");
    if (invDlg.vat_exempt && (!invRecipient.title.trim() || !(invRecipient.vkn.length === 10 || invRecipient.vkn.length === 11)))
      return toast.error("KDV'siz cari — fatura alıcısı ünvanı ve 10/11 haneli VKN/TCKN girin");
    if (invGeneric && recipientError(invRp)) return toast.error(recipientError(invRp));
    try {
      const total = (invDlg.items || []).length;
      const body = { reflect_discount: invMode === "disc", show_brand: invShowBrand };
      if (invItems.length !== total) body.item_indexes = invItems;
      if (invDlg.vat_exempt) { body.recipient_title = invRecipient.title.trim(); body.recipient_vkn = invRecipient.vkn; }
      if (invGeneric) body.recipient = recipientPayload(invRp);
      const { data } = await api.post(`/quotes/${invDlg.id}/generate-invoice`, body);
      if (invGeneric) loadContacts();
      toast.success(`Taslak fatura oluşturuldu (${TRY(data.total)}). e-Fatura sayfasından QNB'ye kesebilir veya silebilirsiniz.`);
      setInvDlg(null); setInvMode("net");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toIrsaliye = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/generate-irsaliye`);
      toast.success(data.existing ? "Bu teklif için irsaliye taslağı zaten var — açılıyor" : "İrsaliye taslağı oluşturuldu · şoför / araç bilgilerini tamamlayıp gönderin");
      setPreview(null);
      navigate(`/irsaliyeler?edit=${data.irsaliye.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openCari = async (q) => {
    try {
      await api.post("/contacts", { title: q.customer_title, type: "musteri", phone: q.customer_phone || "", address: q.customer_address || "" });
      toast.success("Cari hesap açıldı"); loadContacts();
    } catch (e) { toast.error(apiError(e, "Cari zaten mevcut olabilir")); }
  };

  const sendToDepot = async () => {
    try {
      await api.post(`/quotes/${sendDlg.id}/send-to-depot`, { assigned_to: sendWorker === "all" ? null : sendWorker });
      toast.success("Teklif depoya hazırlık listesi olarak gönderildi (fiyatsız)");
      setSendDlg(null); setSendWorker("all");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveTerms = async () => {
    const terms = termsDraft.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.put("/settings/quote-terms", { terms });
      setQuoteTerms(data.terms && data.terms.length ? data.terms : terms);
      setTermsDlg(false);
      toast.success("Teklif koşulları kaydedildi");
    } catch (e) { toast.error(apiError(e)); }
  };

  const importExcel = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        const parsed = rows.map((r) => ({
          name: r["Mal/Hizmet"] || r["Ürün"] || r["name"] || "",
          brand: r["Marka"] || r["brand"] || "",
          description: r["Açıklama"] || r["description"] || "",
          quantity: Number(r["Miktar"] || r["quantity"] || 1),
          unit: String(r["Birim"] || r["unit"] || "ADET").toUpperCase(),
          unit_price: Number(r["Birim Fiyat"] || r["unit_price"] || 0),
          discount_rate: Number(r["İskonto"] || r["discount_rate"] || 0),
        })).filter((i) => i.name);
        if (!parsed.length) return toast.error("Geçerli kalem yok");
        openNew({ ...blankQuote(), items: parsed });
        toast.success(`${parsed.length} kalem yüklendi`);
      } catch { toast.error("Excel okunamadı"); }
    };
    reader.readAsArrayBuffer(file); e.target.value = "";
  };
  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([{ "Marka": "VİKO", "Mal/Hizmet": "Karre Topraklı Priz Beyaz", "Açıklama": "", "Miktar": 10, "Birim": "ADET", "Birim Fiyat": 250, "İskonto": 55 }]);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Teklif");
    XLSX.writeFile(wb, "teklif-sablonu.xlsx");
  };

  // ---- KPI + filtre ----
  const sum = (arr) => arr.reduce((s, q) => s + Number(q.total || 0), 0);
  const byStatus = (s) => list.filter((q) => q.status === s);
  const accepted = byStatus("kabul"), rejected = byStatus("red");
  const pending = list.filter((q) => q.status === "gonderildi" || q.status === "degerlendiriliyor");
  const decided = accepted.length + rejected.length;
  const convRate = decided ? Math.round((accepted.length / decided) * 100) : 0;
  const monthStart = periodStart("month");
  const thisMonth = list.filter((q) => String(q.date || "").slice(0, 10) >= monthStart);

  const filtered = useMemo(() => {
    const s = search.trim().toLocaleLowerCase("tr");
    const from = periodStart(period);
    let arr = list.filter((q) => {
      if (stFilter === "pending" ? !(q.status === "gonderildi" || q.status === "degerlendiriliyor") : (stFilter !== "all" && q.status !== stFilter)) return false;
      if (from && String(q.date || "").slice(0, 10) < from) return false;
      if (s && !((q.customer_title || "").toLocaleLowerCase("tr").includes(s) || (q.quote_no || "").toLowerCase().includes(s))) return false;
      return true;
    });
    if (sort === "amount_desc") arr = [...arr].sort((a, b) => (b.total || 0) - (a.total || 0));
    else if (sort === "date_desc") arr = [...arr].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")) || String(b.quote_no || "").localeCompare(String(a.quote_no || "")));
    else if (sort === "customer_az") arr = [...arr].sort((a, b) => (a.customer_title || "").localeCompare(b.customer_title || "", "tr"));
    return arr;
  }, [list, search, stFilter, period, sort]);

  const filterOptions = [["all", "Tümü", list.length], ["taslak", "Taslak", byStatus("taslak").length], ["pending", "Bekleyen", pending.length], ["kabul", "Kabul", accepted.length], ["red", "Red", rejected.length]];

  const initials = (s) => String(s || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toLocaleUpperCase("tr");
  const ValidityBadge = ({ q }) => {
    const v = validityState(q);
    if (!v) return q.valid_until ? <span className="text-[11px] text-muted-foreground">{fmtDate(q.valid_until)}'e kadar</span> : null;
    if (v.state === "expired") return <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-600 border border-red-200 text-[10px] font-bold px-1.5 py-px" data-testid={`quote-expired-${q.id}`}><AlertTriangle className="h-3 w-3" />Süresi doldu</span>;
    if (v.state === "soon") return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-bold px-1.5 py-px" data-testid={`quote-expiring-${q.id}`}><Hourglass className="h-3 w-3" />{v.days === 0 ? "Bugün son gün" : `${v.days} gün kaldı`}</span>;
    return <span className="text-[11px] text-muted-foreground">{fmtDate(q.valid_until)}'e kadar</span>;
  };
  const StatusSelect = ({ q, st }) => (
    <Select value={q.status} onValueChange={(v) => changeStatus(q, v)} disabled={statusBusy === q.id}>
      <SelectTrigger className={`h-7 w-auto min-w-[140px] rounded-full border text-[11px] font-bold px-2.5 gap-1 [&>svg]:h-3 [&>svg]:w-3 ${st.cls}`} data-testid={`quote-status-${q.id}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /><SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`quote-status-opt-${q.id}-${k}`}><span className="inline-flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${v.dot}`} />{v.label}</span></SelectItem>)}
      </SelectContent>
    </Select>
  );
  const Actions = ({ q, compact = false }) => (
    <div className={cn("flex items-center shrink-0", compact ? "gap-0" : "gap-1 lg:pl-3 lg:border-l flex-wrap lg:justify-end")}>
      <Button size="sm" variant="outline" className={cn("h-8 border-primary/30 text-primary hover:bg-primary/5", compact && "h-7 px-2 mr-1")} onClick={() => setPreview(q)} data-testid={`view-quote-${q.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Önizle</Button>
      <div className="inline-flex items-center rounded-lg border bg-muted/30 p-0.5">
        <Button size="icon" variant="ghost" className="h-7 w-7" title="Düzenle" onClick={() => openEdit(q)} data-testid={`edit-quote-${q.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" title="Depoya Gönder (hazırlık listesi)" onClick={() => { setSendDlg(q); setSendWorker("all"); }} data-testid={`send-depot-${q.id}`}><Warehouse className="h-3.5 w-3.5 text-primary" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" title="İrsaliye Oluştur (e-İrsaliye taslağı)" onClick={() => toIrsaliye(q)} data-testid={`irsaliye-quote-${q.id}`}><Truck className="h-3.5 w-3.5 text-sky-600" /></Button>
        {q.vat_exempt && !(Number(q.kdv) > 0)
          ? <Button size="icon" variant="ghost" className="h-7 w-7" disabled title="KDV'siz cari — resmi fatura kesilemez (KDV'li teklif verin)" data-testid={`invoice-quote-disabled-${q.id}`}><ReceiptText className="h-3.5 w-3.5 text-muted-foreground/40" /></Button>
          : <Button size="icon" variant="ghost" className="h-7 w-7" title={q.vat_exempt ? "Fatura Kes (Taslak) — KDV'li teklif, alıcıya kesilir" : "Fatura Kes (Taslak)"} onClick={() => openInvDlg(q)} data-testid={`invoice-quote-${q.id}`}><ReceiptText className="h-3.5 w-3.5 text-emerald-600" /></Button>}
        <Button size="icon" variant="ghost" className="h-7 w-7" title="Değişiklik Geçmişi" onClick={() => setHistQuote(q)} data-testid={`hist-quote-${q.id}`}><History className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" className="h-7 w-7" title="Sil" onClick={() => del(q.id)} data-testid={`del-quote-${q.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
      </div>
    </div>
  );
  const Badges = ({ q, hasCari }) => (
    <>
      {!hasCari && <span className="text-[10px] font-bold uppercase tracking-wider rounded-full border border-dashed px-1.5 py-px text-muted-foreground" title="Bu ünvan için cari hesap yok">Cari yok</span>}
      {q.restored_from_trash && (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-0.5" data-testid={`restored-badge-${q.id}`}>
          <RotateCcw className="h-3 w-3" />Çöp kutusundan geri getirildi
          <button onClick={() => clearRestored(q.id)} className="ml-0.5 hover:text-amber-900" title="İbareyi kaldır" data-testid={`clear-restored-${q.id}`}><X className="h-3 w-3" /></button>
        </span>
      )}
      {q.vat_exempt && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-0.5" data-testid={`vat-exempt-badge-${q.id}`} title="KDV'siz cari — resmi fatura kesilemez">KDV'siz cari{Number(q.kdv) > 0 ? " · KDV dahil teklif" : ""}</span>}
      {!q.vat_exempt && Number(q.kdv_rate) === 0 && Number(q.net) > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-0.5" data-testid={`kdvsiz-badge-${q.id}`} title="Fiyatlara KDV uygulanmamıştır">KDV'siz</span>}
    </>
  );

  const renderRow = (q) => {
    const st = statusOf(q.status);
    const items = q.items || [];
    const names = items.slice(0, 2).map((it) => [it.brand, it.name].filter(Boolean).join(" "));
    const hasCari = contacts.some((c) => c.title === q.customer_title);
    return (
      <div key={q.id} className="group relative rounded-2xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-primary/40 hover:-translate-y-px" data-testid={`quote-${q.id}`}>
        <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${st.stripe}`} />
        <span className="pointer-events-none absolute -right-20 -top-24 h-48 w-48 rounded-full bg-primary/[0.06] blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
        <div className="relative flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-12 gap-3 min-w-0 items-center">
            <div className="md:col-span-2 min-w-0">
              <div className="font-mono text-[15px] font-bold tabular-nums tracking-tight truncate" data-testid={`quote-no-${q.id}`}>{q.quote_no}</div>
              <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1 flex-wrap"><CalendarDays className="h-3 w-3" />{fmtDate(q.date)}<span className="text-slate-300">·</span><ValidityBadge q={q} /></div>
            </div>
            <div className="md:col-span-4 min-w-0 flex items-center gap-2.5">
              <span className="hidden sm:flex h-9 w-9 shrink-0 rounded-lg bg-[#0B192C] text-white font-head font-black text-xs items-center justify-center">{initials(q.customer_title)}</span>
              <div className="min-w-0">
                <div className="font-head font-black text-[15px] leading-tight truncate" title={q.customer_title}>{q.customer_title}</div>
                <div className="flex flex-wrap items-center gap-1 mt-0.5">
                  {q.customer_phone && <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Phone className="h-3 w-3" />{q.customer_phone}</span>}
                  <Badges q={q} hasCari={hasCari} />
                </div>
              </div>
            </div>
            <div className="md:col-span-2 min-w-0">
              <div className="text-sm font-semibold tabular-nums">{items.length} kalem</div>
              <div className="text-[11px] text-muted-foreground truncate" title={names.join(", ")}>{names.join(", ") || "—"}{items.length > 2 ? ` +${items.length - 2}` : ""}</div>
            </div>
            <div className="md:col-span-2 min-w-0">
              <div className="font-head font-black text-[17px] tabular-nums tracking-tight" data-testid={`quote-total-${q.id}`}>{TRY(q.total)}</div>
              <div className="text-[11px] text-muted-foreground tabular-nums truncate">Net {TRY(q.net)}{Number(q.kdv) > 0 ? ` · KDV %${NUM(q.kdv_rate)}` : ""}{Number(q.global_discount_rate) > 0 ? ` · İsk %${NUM(q.global_discount_rate)}` : ""}</div>
            </div>
            <div className="md:col-span-2 min-w-0">
              <StatusSelect q={q} st={st} />
              {q.updated_by && <div className="text-[10px] text-muted-foreground mt-1 truncate">Son: {q.updated_by}</div>}
            </div>
          </div>
          <Actions q={q} />
        </div>
      </div>
    );
  };

  const renderTable = () => (
    <div className="rounded-2xl border bg-card shadow-sm overflow-hidden" data-testid="quote-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead>
            <tr className="bg-[#0B192C] text-white text-[10px] uppercase tracking-wider font-head h-9">
              <th className="px-3 text-left">Teklif No</th><th className="px-3 text-left">Tarih · Geçerlilik</th><th className="px-3 text-left">Müşteri</th><th className="px-3 text-right">Kalem</th><th className="px-3 text-right">Net</th><th className="px-3 text-right">Tutar</th><th className="px-3 text-left">Durum</th><th className="px-3 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((q) => {
              const st = statusOf(q.status);
              const hasCari = contacts.some((c) => c.title === q.customer_title);
              return (
                <tr key={q.id} className="h-12 border-b border-slate-200 hover:bg-primary/[0.03] transition-colors" data-testid={`quote-${q.id}`}>
                  <td className="px-3"><div className="flex items-center gap-2"><span className={`h-6 w-1 rounded ${st.stripe}`} /><span className="font-mono font-bold tabular-nums" data-testid={`quote-no-${q.id}`}>{q.quote_no}</span></div></td>
                  <td className="px-3 text-xs text-muted-foreground whitespace-nowrap"><span className="tabular-nums">{fmtDate(q.date)}</span> <span className="text-slate-300 mx-1">·</span> <ValidityBadge q={q} /></td>
                  <td className="px-3 min-w-0 max-w-[320px]"><div className="font-head font-bold truncate" title={q.customer_title}>{q.customer_title}</div><div className="flex flex-wrap gap-1 mt-0.5"><Badges q={q} hasCari={hasCari} /></div></td>
                  <td className="px-3 text-right tabular-nums">{(q.items || []).length}</td>
                  <td className="px-3 text-right tabular-nums text-muted-foreground whitespace-nowrap">{TRY(q.net)}</td>
                  <td className="px-3 text-right font-head font-black tabular-nums whitespace-nowrap" data-testid={`quote-total-${q.id}`}>{TRY(q.total)}</td>
                  <td className="px-3"><StatusSelect q={q} st={st} /></td>
                  <td className="px-3"><div className="flex justify-end"><Actions q={q} compact /></div></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 stagger-in" data-testid="quotes-page">
      <PageHeader title="Teklifler" badge="Satış Hattı" subtitle="Profesyonel teklif şablonu · PDF, yazdır, faturaya/irsaliyeye dönüştür, depoya gönder · durumu listeden değiştir">
        <Button variant="outline" onClick={downloadTemplate} data-testid="quote-template-btn"><FileDown className="h-4 w-4 mr-2" />Şablon</Button>
        <label>
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} data-testid="quote-import" />
          <Button variant="outline" asChild><span><FileUp className="h-4 w-4 mr-2" />Excel'den Ekle</span></Button>
        </label>
        <Button variant="outline" onClick={() => { setTermsDraft((quoteTerms || DEFAULT_TERMS).join("\n")); setTermsDlg(true); }} data-testid="quote-terms-btn"><Settings className="h-4 w-4 mr-2" />Teklif Ayarları</Button>
        <Button onClick={() => openNew()} data-testid="add-quote-btn"><Plus className="h-4 w-4 mr-2" />Yeni Teklif</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="quote-kpis">
        <StatCard label="Toplam Teklif" value={list.length} icon={FileText} sub={`${thisMonth.length} teklif bu ay · ${TRY(sum(thisMonth))}`} onClick={() => setStFilter("all")} active={stFilter === "all"} testid="quote-kpi-total" />
        <StatCard label="Bekleyen" value={pending.length} icon={Clock} tone="blue" sub={`Gönderildi + Değerlendiriliyor · ${TRY(sum(pending))}`} onClick={() => setStFilter("pending")} active={stFilter === "pending"} testid="quote-kpi-pending" />
        <StatCard label="Kabul Edilen" value={accepted.length} icon={CheckCircle2} tone="success" sub={TRY(sum(accepted))} onClick={() => setStFilter("kabul")} active={stFilter === "kabul"} testid="quote-kpi-accepted" />
        <StatCard label="Reddedilen" value={rejected.length} icon={XCircle} tone="danger" sub={TRY(sum(rejected))} onClick={() => setStFilter("red")} active={stFilter === "red"} testid="quote-kpi-rejected" />
        <StatCard label="Dönüşüm Oranı" value={`%${convRate}`} icon={TrendingUp} tone="primary" sub={decided ? `${accepted.length} kabul / ${decided} karara bağlanan` : "Henüz karara bağlanan teklif yok"} testid="quote-kpi-conversion" />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative w-full lg:max-w-sm" data-testid="quote-search-wrap">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cari ünvanı veya teklif no ile ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="quote-search" />
        </div>
        <div className="flex items-center gap-2 flex-wrap lg:ml-auto">
          <Segmented value={stFilter} onChange={setStFilter} options={filterOptions} testid="quote-filter" />
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-36" data-testid="quote-period"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-9 w-44" data-testid="quote-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="updated">Son güncellenen üstte</SelectItem>
              <SelectItem value="date_desc">Tarih: Yeni → Eski</SelectItem>
              <SelectItem value="amount_desc">Tutar: Yüksek → Düşük</SelectItem>
              <SelectItem value="customer_az">Müşteri: A → Z</SelectItem>
            </SelectContent>
          </Select>
          <div className="inline-flex rounded-lg border bg-muted/40 p-0.5" data-testid="quote-view">
            <button type="button" onClick={() => setView("card")} className={cn("h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors", view === "card" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")} title="Kart görünümü" data-testid="quote-view-card"><LayoutList className="h-4 w-4" /></button>
            <button type="button" onClick={() => setView("table")} className={cn("h-8 w-8 rounded-md inline-flex items-center justify-center transition-colors", view === "table" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")} title="Tablo görünümü" data-testid="quote-view-table"><Table2 className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground px-1" data-testid="quote-list-summary">
        <span><b className="text-foreground tabular-nums">{filtered.length}</b> teklif listeleniyor{stFilter !== "all" || period !== "all" || search ? " (süzülmüş)" : ""}</span>
        <span className="tabular-nums">Liste toplamı: <b className="text-foreground">{TRY(sum(filtered))}</b></span>
      </div>

      {filtered.length === 0 ? (
        <Empty title={list.length ? "Eşleşen teklif yok" : "Henüz teklif yok"} desc={list.length ? "Filtreyi, dönemi veya aramayı değiştirin." : "Yeni teklif oluşturun veya Excel'den kalem ekleyin."}
          action={list.length ? <Button variant="outline" onClick={() => { setStFilter("all"); setPeriod("all"); setSearch(""); }} data-testid="quote-filter-reset">Filtreleri sıfırla</Button> : <Button onClick={() => openNew()} data-testid="quote-empty-new"><Plus className="h-4 w-4 mr-2" />Yeni Teklif</Button>} />
      ) : view === "table" ? renderTable() : (
        <div className="space-y-2.5" data-testid="quote-list">{filtered.map(renderRow)}</div>
      )}

      <StockSourceDialog quote={srcDlg?.q || null} onCancel={() => { srcDlg?.resolve(null); setSrcDlg(null); }} onConfirm={(r) => { srcDlg?.resolve(r); setSrcDlg(null); }} />
      <QuoteEditorDialog open={editor.open} onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))} initial={editor.initial} contacts={contacts} onSaved={onSaved} onContactsChanged={loadContacts} />

      {/* Send to depot */}
      <Dialog open={!!sendDlg} onOpenChange={(o) => !o && setSendDlg(null)}>
        <DialogContent className="max-w-md" data-testid="send-depot-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><Truck className="h-6 w-6 text-primary" />Depoya Gönder</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2"><b>{sendDlg?.quote_no}</b> teklifi hazırlık listesi olarak gönderilecek. <b>Fiyatlar depo personeline gösterilmez</b>, sadece malzeme ve miktarlar görünür.</p>
          <F label="Kime gönderilsin?">
            <Select value={sendWorker} onValueChange={setSendWorker}>
              <SelectTrigger data-testid="send-worker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Depo Yöneticileri</SelectItem>
                {workers.map((w) => <SelectItem key={w.id} value={w.id}><span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{w.name}</span></SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDlg(null)}>İptal</Button>
            <Button onClick={sendToDepot} data-testid="confirm-send-depot"><Truck className="h-4 w-4 mr-2" />Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuotePreviewDialog quote={preview} onClose={() => setPreview(null)} terms={quoteTerms}>
        <Button size="sm" variant="outline" onClick={() => { setSendDlg(preview); setSendWorker("all"); }} data-testid="preview-depot-btn"><Warehouse className="h-4 w-4 mr-1.5" />Depoya</Button>
        <Button size="sm" variant="outline" onClick={() => toIrsaliye(preview)} data-testid="preview-irsaliye-btn"><Truck className="h-4 w-4 mr-1.5" />İrsaliye</Button>
        {preview && (!preview.vat_exempt || Number(preview.kdv) > 0) && <Button size="sm" variant="outline" onClick={() => { const q = preview; setPreview(null); openInvDlg(q); }} data-testid="preview-invoice-btn"><ReceiptText className="h-4 w-4 mr-1.5" />Fatura</Button>}
        <Button size="sm" variant="outline" onClick={() => { const q = preview; setPreview(null); openEdit(q); }} data-testid="preview-edit-btn"><Pencil className="h-4 w-4 mr-1.5" />Düzenle</Button>
        {preview && !contacts.some((c) => c.title === preview.customer_title) && <Button size="sm" variant="outline" onClick={() => openCari(preview)} data-testid="cari-btn"><UserPlus className="h-4 w-4 mr-1.5" />Cari Aç</Button>}
      </QuotePreviewDialog>

      {/* Fatura Kes (Taslak) */}
      <Dialog open={!!invDlg} onOpenChange={(o) => !o && setInvDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><ReceiptText className="h-6 w-6 text-emerald-600" />Fatura Kes (Taslak)</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2"><b>{invDlg?.quote_no}</b> teklifinden dahili bir <b>Taslak</b> fatura oluşturulacak. İstemezseniz silebilir; hazır olduğunuzda <b>Fatura Kes</b> sayfasından QNB'ye kesebilirsiniz. (Taslak, QNB'ye gönderilene kadar cari bakiyeye yansımaz.)</p>
          <F label="Faturada fiyatlar nasıl görünsün?">
            <Select value={invMode} onValueChange={setInvMode}>
              <SelectTrigger data-testid="invoice-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="net">Net Fiyatlar (iskonto uygulanmış, iskonto görünmez)</SelectItem>
                <SelectItem value="disc">İskontolu (liste fiyatı + her kalemin kendi iskontosu görünür)</SelectItem>
              </SelectContent>
            </Select>
          </F>
          {invGeneric && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3 space-y-2" data-testid="invoice-generic-recipient-block">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-amber-900 font-head">Fatura Alıcısı (zorunlu)</div>
              <p className="text-[11px] text-amber-800"><b>{invDlg.customer_title}</b> genel müşteri tanımıdır — fatura bu cari adına oluşturulmaz. Kayıtlı bir cari seçin ya da alıcının vergi bilgileriyle yeni cari açın; taslak fatura o cari adına oluşturulur.</p>
              <InvoiceRecipientPicker value={invRp} onChange={setInvRp} contacts={contacts} data-testid="invoice-recipient" />
            </div>
          )}
          {invDlg?.vat_exempt && (
            <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3 space-y-2" data-testid="invoice-recipient-block">
              <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-sky-800 font-head">Fatura Alıcısı (KDV'siz cari — KDV'li teklif)</div>
              <p className="text-[11px] text-sky-700">Bu cari KDV'siz olduğundan fatura, girdiğiniz alıcıya (mevcut cari ünvanı ya da elle) kesilir; cari değişmez.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label className="text-[10px] uppercase text-muted-foreground">Alıcı Ünvanı *</Label><Input value={invRecipient.title} onChange={(e) => setInvRecipient((r) => ({ ...r, title: e.target.value }))} placeholder="Fatura kesilecek ünvan" data-testid="invoice-recipient-title" /></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">VKN / TCKN *</Label><Input value={invRecipient.vkn} onChange={(e) => setInvRecipient((r) => ({ ...r, vkn: e.target.value.replace(/\D/g, "") }))} maxLength={11} className="tabular-nums" placeholder="10 veya 11 haneli" data-testid="invoice-recipient-vkn" /></div>
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between border-b pb-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Faturaya eklenecek kalemler</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{invItems.length}/{(invDlg?.items || []).length} seçili</span>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setInvItems((invDlg?.items || []).map((_, i) => i))} data-testid="invoice-items-all">Tümü</Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setInvItems([])} data-testid="invoice-items-none">Hiçbiri</Button>
              </div>
            </div>
            <div className="space-y-1.5 max-h-[38vh] overflow-y-auto">
              {(invDlg?.items || []).map((it, i) => (
                <label key={i} className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer ${invItems.includes(i) ? "bg-emerald-50 border-emerald-300" : ""}`} data-testid={`invoice-item-row-${i}`}>
                  <Checkbox checked={invItems.includes(i)} onCheckedChange={() => toggleInvItem(i)} data-testid={`invoice-item-check-${i}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{[it.brand, it.name].filter(Boolean).join(" ")}</div>
                    <div className="text-xs text-muted-foreground">{NUM(it.quantity)} {it.unit} × {TRY(it.unit_price)}{Number(it.discount_rate) ? ` · %${NUM(it.discount_rate)} isk.` : ""}</div>
                  </div>
                  <span className="font-bold tabular-nums whitespace-nowrap text-sm">{TRY(Number(it.quantity) * Number(it.unit_price) * (1 - Number(it.discount_rate || 0) / 100))}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="flex items-start gap-2 rounded-lg border p-2.5 text-xs cursor-pointer" data-testid="invoice-show-brand">
            <Checkbox checked={invShowBrand} onCheckedChange={(v) => setInvShowBrand(!!v)} className="mt-0.5" />
            <span>Faturada ürün adının başına <b>marka adı</b> eklensin (ör. "ÖZNUR 4X10 NYY KABLO").</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvDlg(null)}>İptal</Button>
            <Button onClick={generateInvoice} data-testid="invoice-generate"><ReceiptText className="h-4 w-4 mr-2" />Taslak Fatura Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Değişiklik Geçmişi */}
      <Dialog open={!!histQuote} onOpenChange={(o) => !o && setHistQuote(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="quote-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2"><History className="h-5 w-5 text-primary" />Değişiklik Geçmişi</DialogTitle>
            {histQuote && <p className="text-sm text-muted-foreground">{histQuote.quote_no} · {histQuote.customer_title}</p>}
          </DialogHeader>
          {histQuote && <RecordHistory entityType="quote" entityId={histQuote.id} className="mt-2" />}
        </DialogContent>
      </Dialog>

      {/* Teklif Ayarları — Koşullar & Notlar */}
      <Dialog open={termsDlg} onOpenChange={setTermsDlg}>
        <DialogContent className="max-w-lg" data-testid="quote-terms-dialog">
          <DialogHeader>
            <DialogTitle className="font-head text-xl flex items-center gap-2"><Settings className="h-5 w-5 text-primary" />Teklif Koşulları & Notlar</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Her satır, teklifin <b>"Koşullar &amp; Notlar"</b> bölümünde ayrı bir madde olur. Özel yer tutucular: <code className="px-1 rounded bg-muted">{"{ODEME}"}</code> → seçilen ödeme koşuluna göre, <code className="px-1 rounded bg-muted">{"{KDV}"}</code> → KDV durumuna göre otomatik metne dönüşür.</p>
          <Textarea rows={9} value={termsDraft} onChange={(e) => setTermsDraft(e.target.value)} placeholder={"Her satıra bir koşul yazın...\nÖrn: Teklifimiz 1 iş günü geçerlidir."} data-testid="quote-terms-textarea" className="text-sm" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermsDraft(DEFAULT_TERMS.join("\n"))} data-testid="quote-terms-reset">Varsayılana Dön</Button>
            <Button onClick={saveTerms} data-testid="quote-terms-save"><Settings className="h-4 w-4 mr-2" />Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
